import { DOCUMENT, Location, isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    EffectCleanupRegisterFn,
    PLATFORM_ID,
    Signal,
    WritableSignal,
    afterNextRender,
    computed,
    effect,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Data, ParamMap } from '@angular/router';

import { BlockDirective, ConcatClassesPipe, ElemDirective, ModDirective } from '@rt-tools/core';
import { NotificationBus, RtButtonDirective, RtSpinnerComponent } from '@rt-tools/ui-kit-v2';
import { IParamChangeEvent, ParamsPanelComponent, ResultsPanelComponent } from '@stair-craft/calc/ui';
import { PlanViewComponent, ViewportComponent } from '@stair-craft/calc/ui';
import type { IOpeningDragEvent } from '@stair-craft/calc/ui';
import { readStairConfigFile, stairConfigLoadErrorMessage, writeDxf, writePartsArchive } from '@stair-craft/calc/ui';
import { StairConfigSessionService } from '@stair-craft/calc/util';
import { AnalyticsService, EAnalyticsGoal } from '@stair-craft/common/analytics';
import {
    DEFAULT_STAIR_CONFIG,
    IStairCalculationResult,
    IStairConfig,
    IStairType,
    decodeStairConfigFromParams,
    encodeStairConfigToParams,
} from '@stair-craft/common/util';

const VALID_STAIR_TYPES: ReadonlySet<IStairType> = new Set<IStairType>(['straight', 'l-shape', 'u-shape', 'spiral']);

const STAIR_TYPE_TO_PATH: Readonly<Record<IStairType, string>> = {
    straight: '/calc/straight',
    'l-shape': '/calc/l',
    'u-shape': '/calc/u',
    spiral: '/calc/spiral',
};

function parseStairType(raw: string | null | undefined): IStairType | null {
    return raw != null && VALID_STAIR_TYPES.has(raw as IStairType) ? (raw as IStairType) : null;
}

export enum EViewMode {
    ThreeD = '3d',
    Plan = 'plan',
    Side = 'side',
}

const CALCULATOR_RESULT_DEBOUNCE_MS: number = 800;
const COPY_FEEDBACK_MS: number = 2000;
const BEM_BLOCK: string = 'sc-constructor-page';
/** The end of the parts archive name, after the drawing's own: `staircraft-straight-2026-10-26-детали.zip`. */
const PARTS_SUFFIX: string = $localize`:@@exportPartsSuffix:детали`;

// The third column opens here: the 360 and 380 side columns still leave the canvas 540.
// Mirrored by the same number in the component stylesheet.
const THREE_COLUMN_QUERY: string = '(min-width: 1280px)';

@Component({
    selector: 'sc-constructor-page',
    templateUrl: './constructor-page.component.html',
    styleUrl: './constructor-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // rt-tools
        BlockDirective,
        ElemDirective,
        ConcatClassesPipe,
        ModDirective,
        RtButtonDirective,
        RtSpinnerComponent,
        ViewportComponent,
        PlanViewComponent,
        ParamsPanelComponent,
        ResultsPanelComponent,
    ],
    providers: [StairConfigSessionService],
    host: {
        class: BEM_BLOCK,
    },
})
export default class ConstructorPageComponent {
    readonly #session: StairConfigSessionService = inject(StairConfigSessionService);
    readonly #analytics: AnalyticsService = inject(AnalyticsService);
    readonly #platformId: object = inject(PLATFORM_ID);
    readonly #route: ActivatedRoute = inject(ActivatedRoute);
    readonly #doc: Document = inject(DOCUMENT);
    readonly #location: Location = inject(Location);
    readonly #notifications: NotificationBus = inject(NotificationBus);
    readonly #destroyRef: DestroyRef = inject(DestroyRef);

    #copyResetTimer: ReturnType<typeof setTimeout> | null = null;

    protected readonly planViewRef: Signal<PlanViewComponent | undefined> = viewChild<PlanViewComponent>('planView');

    protected readonly ViewMode: typeof EViewMode = EViewMode;
    protected readonly viewMode: WritableSignal<EViewMode> = signal<EViewMode>(EViewMode.ThreeD);
    protected readonly isWide: WritableSignal<boolean> = signal<boolean>(false);
    protected readonly justCopied: WritableSignal<boolean> = signal<boolean>(false);

    // The session owns config state, transitions, persistence, and the derived result.
    protected readonly config: Signal<IStairConfig> = this.#session.config;
    protected readonly result: Signal<IStairCalculationResult | null> = this.#session.result;
    protected readonly errorMessage: Signal<string | null> = this.#session.error;

    #initializeSession(): void {
        const snapshot: ActivatedRoute['snapshot'] = this.#route.snapshot;
        const initialType: IStairType =
            parseStairType(snapshot.data['stairType'] as string | undefined) ??
            parseStairType(snapshot.queryParamMap.get('t')) ??
            parseStairType(snapshot.queryParamMap.get('type')) ??
            DEFAULT_STAIR_CONFIG.type;
        this.#session.initialize(initialType, decodeStairConfigFromParams(snapshot.queryParamMap));
    }

    // Visually hidden H1 — accessibility/SEO require exactly one H1 per page.
    // Localized per stair type so each /calc/* route exposes a distinct heading.
    protected readonly pageTitle: Signal<string> = computed((): string => {
        switch (this.config().type) {
            case 'l-shape':
                return $localize`:@@calcH1LShape:Конструктор Г-образной лестницы`;
            case 'u-shape':
                return $localize`:@@calcH1UShape:Конструктор П-образной лестницы`;
            case 'spiral':
                return $localize`:@@calcH1Spiral:Конструктор винтовой лестницы`;
            default:
                return $localize`:@@calcH1Straight:Конструктор прямой лестницы`;
        }
    });

    constructor() {
        // Seed the session from the route on both server and client so the prerendered
        // HTML reflects the requested stair type and any shared URL config.
        this.#initializeSession();

        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }

        this.#route.data.pipe(takeUntilDestroyed()).subscribe((data: Data): void => {
            const requested: IStairType | null = parseStairType(data['stairType'] as string | undefined);
            if (requested !== null && requested !== this.config().type) {
                this.onConfigChange({ ...this.config(), type: requested });
            }
        });

        this.#route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params: ParamMap): void => {
            const requested: IStairType | null = parseStairType(params.get('t')) ?? parseStairType(params.get('type'));
            if (requested !== null && requested !== this.config().type) {
                this.onConfigChange({ ...this.config(), type: requested });
            }
        });

        afterNextRender((): void => {
            this.#analytics.trackGoal(EAnalyticsGoal.CalculatorOpen, { stair_type: this.config().type });
        });

        afterNextRender((): void => {
            const mql: MediaQueryList = globalThis.matchMedia(THREE_COLUMN_QUERY);
            this.isWide.set(mql.matches);
            const listener: (event: MediaQueryListEvent) => void = (event: MediaQueryListEvent): void => {
                this.isWide.set(event.matches);
            };
            mql.addEventListener('change', listener);
            this.#destroyRef.onDestroy((): void => {
                mql.removeEventListener('change', listener);
            });
        });

        effect((onCleanup: EffectCleanupRegisterFn): void => {
            const result: IStairCalculationResult | null = this.result();
            if (!result || this.errorMessage()) {
                return;
            }
            const stairType: IStairConfig['type'] = this.config().type;
            const resultSnapshot: IStairCalculationResult = result;
            const timer: ReturnType<typeof setTimeout> = setTimeout((): void => {
                this.#analytics.trackGoal(EAnalyticsGoal.CalculatorResult, {
                    stair_type: stairType,
                    step_count: resultSnapshot.stepCount,
                    stair_angle: resultSnapshot.stairAngle,
                    total_run: resultSnapshot.totalRun,
                    blondel_value: resultSnapshot.blondelValue,
                    total_cost: resultSnapshot.estimatedCostUsd,
                    currency: 'USD',
                    is_valid: resultSnapshot.isValid,
                });
            }, CALCULATOR_RESULT_DEBOUNCE_MS);
            onCleanup((): void => {
                clearTimeout(timer);
            });
        });

        effect((): void => {
            // Reflect current config in the URL as readable short-key query params.
            // Location.replaceState updates the address bar without triggering a
            // router navigation, so it does not retrigger ActivatedRoute streams
            // and the component instance is preserved.
            const config: IStairConfig = this.config();
            const params: Record<string, string> = encodeStairConfigToParams(config);
            const search: string = new URLSearchParams(params).toString();
            const path: string = STAIR_TYPE_TO_PATH[config.type];
            this.#location.replaceState(search ? `${path}?${search}` : path);
        });

        effect((): void => {
            const error: string | null = this.errorMessage();
            if (error !== null) {
                this.#analytics.trackGoal(EAnalyticsGoal.AppError, { message: error });
            }
        });

        this.#destroyRef.onDestroy((): void => {
            if (this.#copyResetTimer !== null) {
                globalThis.clearTimeout(this.#copyResetTimer);
                this.#copyResetTimer = null;
            }
        });
    }

    protected setViewMode(mode: EViewMode): void {
        this.viewMode.set(mode);
    }

    protected onBarFileSelected(event: Event): void {
        const picker: HTMLInputElement = event.target as HTMLInputElement;
        const file: File | undefined = picker.files?.[0];
        if (file) {
            readStairConfigFile(file).then(
                (loaded: IStairConfig): void => {
                    this.onConfigChange(loaded);
                },
                (error: unknown): void => {
                    this.#notifications.error(stairConfigLoadErrorMessage(error));
                }
            );
        }
        picker.value = '';
    }

    protected onShare(): void {
        if (this.justCopied()) {
            return;
        }
        this.#analytics.trackGoal(EAnalyticsGoal.ShareClick, { channel: 'link' });
        const url: string = globalThis.location.href;
        globalThis.navigator.clipboard.writeText(url).then(
            (): void => {
                this.#notifications.success($localize`:@@urlCopied:Ссылка скопирована`);
                this.justCopied.set(true);
                if (this.#copyResetTimer !== null) {
                    globalThis.clearTimeout(this.#copyResetTimer);
                }
                this.#copyResetTimer = globalThis.setTimeout((): void => {
                    this.justCopied.set(false);
                    this.#copyResetTimer = null;
                }, COPY_FEEDBACK_MS);
            },
            (): void => {
                this.#notifications.error($localize`:@@urlCopyFailed:Не удалось скопировать ссылку`);
            }
        );
    }

    protected onExport(): void {
        const stairConfig: IStairConfig = this.config();
        const baseName: string = this.#exportBaseName(stairConfig);

        const jsonBlob: Blob = new Blob([JSON.stringify(stairConfig, null, 2)], { type: 'application/json' });
        this.#downloadBlob(jsonBlob, `${baseName}.json`);
        this.#analytics.trackGoal(EAnalyticsGoal.ExportClick, { kind: 'json', stair_type: stairConfig.type });

        // The PNG is the whole fixed sheet, drawn apart from the screen: the window size and the view do not change it.
        void this.planViewRef()
            ?.exportSheetPng()
            .then((pngBlob: Blob | null) => {
                if (pngBlob) {
                    this.#downloadBlob(pngBlob, `${baseName}.png`);
                    this.#analytics.trackGoal(EAnalyticsGoal.ExportClick, { kind: 'png', stair_type: stairConfig.type });
                }
            });
    }

    // A result that fails the norms still exports, as `.json` and `.png` do; only a missing one does not.
    protected onExportDxf(): void {
        const result: IStairCalculationResult | null = this.result();
        if (!result) {
            return;
        }
        const stairConfig: IStairConfig = this.config();
        const dxfBlob: Blob = new Blob([writeDxf(result, stairConfig, new Date())], { type: 'application/dxf' });
        this.#downloadBlob(dxfBlob, `${this.#exportBaseName(stairConfig)}.dxf`);
        this.#analytics.trackGoal(EAnalyticsGoal.ExportClick, { kind: 'dxf', stair_type: stairConfig.type });
    }

    // The parts archive goes out under the same name as the drawing, with the parts suffix.
    protected onExportParts(): void {
        const result: IStairCalculationResult | null = this.result();
        if (!result) {
            return;
        }
        const stairConfig: IStairConfig = this.config();
        const archive: Blob = new Blob([writePartsArchive(result, stairConfig, new Date())], { type: 'application/zip' });
        this.#downloadBlob(archive, `${this.#exportBaseName(stairConfig)}-${PARTS_SUFFIX}.zip`);
        this.#analytics.trackGoal(EAnalyticsGoal.ExportClick, { kind: 'dxf-parts', stair_type: stairConfig.type });
    }

    /** One file name for every export of a calculation — `.json`, `.png` and `.dxf` differ only by extension. */
    #exportBaseName(stairConfig: IStairConfig): string {
        const dateStr: string = new Date().toISOString().slice(0, 10);
        return `staircraft-${stairConfig.type}-${dateStr}`;
    }

    #downloadBlob(blob: Blob, filename: string): void {
        const url: string = URL.createObjectURL(blob);
        const anchor: HTMLAnchorElement = this.#doc.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        URL.revokeObjectURL(url);
    }

    protected onConfigChange(newConfig: IStairConfig): void {
        const { typeChanged, from, to }: { typeChanged: boolean; from: IStairType; to: IStairType } = this.#session.applyConfig(newConfig);
        if (typeChanged) {
            this.#analytics.trackGoal(EAnalyticsGoal.CalculatorTypeChange, { from, to });
        }
    }

    protected onConfigReset(): void {
        this.#session.reset();
    }

    protected onParamChanged(event: IParamChangeEvent): void {
        this.#analytics.trackGoal(EAnalyticsGoal.CalculatorParamChange, { field: event.field, value: event.value });
    }

    protected onFeedbackOpened(): void {
        this.#analytics.trackGoal(EAnalyticsGoal.FeedbackOpen);
    }

    protected onFeedbackSubmitted(): void {
        this.#analytics.trackGoal(EAnalyticsGoal.FeedbackSubmitSuccess);
    }

    protected onFeedbackFailed(event: { reason: string }): void {
        this.#analytics.trackGoal(EAnalyticsGoal.FeedbackSubmitError, { reason: event.reason });
    }

    protected onOpeningDrag(event: IOpeningDragEvent): void {
        this.#session.applyOpeningDrag(event.openingX, event.openingZ);
    }
}
